import { useEffect, useRef } from 'react';

// Compteur d'instances : chaque montage du hook obtient son propre identifiant.
// Sans lui, deux composants qui écrivent dans le <head> sur la même page
// (SEOHead + le preload LCP d'Article, par exemple) s'effaceraient
// mutuellement, puisque le nettoyage cible l'attribut de marquage.
let INSTANCE = 0;

/**
 * useDocumentHead — écrit titre / meta / link / JSON-LD dans le <head>.
 *
 * POURQUOI CE HOOK REMPLACE react-helmet-async
 * Mesuré le 2026-07-19, en dev ET sur le build de production : Helmet
 * n'émettait RIEN. `document.head.querySelectorAll('[data-rh]').length` valait
 * 0 sur toutes les routes, aucune mutation du <head> n'était observée au
 * changement de page, et chaque page SPA servait donc le titre, la description
 * et le canonical de repli d'index.html — canonical pointant sur « / » pour
 * les 42 pages qui utilisent <SEOHead>.
 *
 * Le provider était pourtant correctement monté (main.jsx) et le paquet
 * correctement résolu (une seule copie dans node_modules). react-helmet-async
 * 2.0.5 n'est plus maintenu depuis 2023 ; plutôt que de poursuivre le
 * diagnostic d'une dépendance morte, on écrit les ~50 lignes qui font le
 * travail, sans dépendance et sans magie.
 *
 * CONTRAT
 * Tous les nœuds posés portent `data-ip-head`, et sont retirés puis réécrits à
 * chaque changement — c'est ce qui empêche les balises d'une page de fuir sur
 * la suivante lors d'une navigation client. Les balises statiques d'index.html
 * (sans l'attribut) sont retirées quand ce hook fournit leur équivalent, sinon
 * la page se retrouverait avec deux canonical ou deux description.
 */

const MARK = 'data-ip-head';

/** Retire les seuls noeuds posés par CETTE instance du hook. */
function clear(id) {
  document.head.querySelectorAll(`[${MARK}="${id}"]`).forEach((n) => n.remove());
}

/** Échappe une valeur d'attribut pour un sélecteur CSS. */
const q = (v) => String(v).replace(/["\\]/g, '\\$&');

/**
 * Retire la balise STATIQUE d'index.html que celle-ci remplace.
 *
 * Indispensable, et pas seulement pour le canonical : index.html porte déjà un
 * jeu complet d'og:*, twitter:* et de hreflang de repli. Sans cette purge, la
 * page se retrouve avec deux `og:title` — et les crawlers lisent le premier,
 * donc le titre générique du site, ce qui annule le bénéfice de la page.
 */
function dropStatic(el) {
  let selector = null;
  if (el.tagName === 'META') {
    const name = el.getAttribute('name');
    const prop = el.getAttribute('property');
    if (name) selector = `meta[name="${q(name)}"]`;
    else if (prop) selector = `meta[property="${q(prop)}"]`;
  } else if (el.tagName === 'LINK') {
    const rel = el.getAttribute('rel');
    // Les alternates vont par lot : on retire TOUS les statiques d'un coup,
    // sinon les hreflang de repli s'additionnent aux nôtres.
    if (rel === 'alternate') selector = 'link[rel="alternate"]';
    else if (rel) selector = `link[rel="${q(rel)}"]`;
  }
  if (!selector) return;
  document.head.querySelectorAll(`${selector}:not([${MARK}])`).forEach((n) => n.remove());
}

function put(id, tag, attrs, text) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    el.setAttribute(k, String(v));
  }
  if (text != null) el.textContent = text;
  el.setAttribute(MARK, id);
  document.head.appendChild(el);
  return el;
}

/**
 * @param {object} spec
 * @param {string} [spec.title]
 * @param {string} [spec.lang]                    posé sur <html lang>
 * @param {Array<Record<string,string>>} [spec.meta]  attributs bruts d'un <meta>
 * @param {Array<Record<string,string>>} [spec.link]  attributs bruts d'un <link>
 * @param {Array<object>} [spec.jsonLd]           objets sérialisés en ld+json
 */
export default function useDocumentHead(spec) {
  // Identifiant stable pour la durée de vie du composant.
  const idRef = useRef(null);
  if (idRef.current === null) { INSTANCE += 1; idRef.current = String(INSTANCE); }
  const id = idRef.current;

  // Sérialisé : le spec est reconstruit à chaque rendu, une dépendance par
  // référence relancerait l'effet en boucle.
  const key = JSON.stringify(spec ?? {});

  useEffect(() => {
    const s = JSON.parse(key);
    clear(id);

    if (s.lang) document.documentElement.setAttribute('lang', s.lang);

    if (s.title) {
      // <title> est unique : on écrase plutôt que d'en ajouter un second.
      let el = document.head.querySelector('title');
      if (!el) el = document.head.appendChild(document.createElement('title'));
      el.textContent = s.title;
    }

    // Purge d'abord, écrit ensuite : dropStatic() ne vise que les nœuds NON
    // marqués, mais l'inverse retirerait les alternates qu'on vient de poser.
    for (const m of s.meta ?? []) dropStatic(put(id, 'meta', m));
    for (const l of s.link ?? []) dropStatic(put(id, 'link', l));

    for (const obj of s.jsonLd ?? []) {
      if (!obj) continue;
      // Purge la copie STATIQUE d'index.html du meme @type avant d'ecrire la
      // notre : index.html embarque WebSite + NewsMediaOrganization pour les
      // crawlers qui n'executent pas le JS, et GlobalSchema les reemet. Sans
      // cette purge, la page declare deux fois la meme entite.
      const type = obj['@type'];
      if (type) {
        document.head
          .querySelectorAll(`script[type="application/ld+json"]:not([${MARK}])`)
          .forEach((n) => {
            try {
              if (JSON.parse(n.textContent)['@type'] === type) n.remove();
            } catch { /* JSON-LD illisible : on n'y touche pas */ }
          });
      }
      put(id, 'script', { type: 'application/ld+json' }, JSON.stringify(obj));
    }

    return () => clear(id);
  }, [key, id]);
}
